import { useState } from 'react';
import { useEmployees } from '@/api/queries/employee.queries';
import { useMonthlyAttendance } from '@/api/queries/attendance.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { cn } from '@/lib/cn';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay } from 'date-fns';

const STATUS_COLOR = {
  PRESENT: 'bg-green-500',
  ABSENT: 'bg-red-400',
  LATE: 'bg-yellow-400',
  ON_LEAVE: 'bg-blue-400',
  HALF_DAY: 'bg-orange-400',
};


function EmployeeRow({ emp, month, days }) {
  const { data: resp } = useMonthlyAttendance({ employee_id: emp.id, month });
  const records = resp?.data || [];
  const byDate = Object.fromEntries(records.map(r => [r.date?.slice(0, 10), r.status]));

  return (
    <tr className="hover:bg-[var(--bg-muted)]">
      <td className="px-4 py-2 sticky left-0 bg-[var(--bg-card)] z-10 border-r border-[var(--border)] min-w-[180px]">
        <div className="flex items-center gap-2">
          <Avatar name={`${emp.first_name} ${emp.last_name}`} size="xs"/>
          <span className="text-[12px] font-medium text-[var(--text-primary)] truncate">{emp.first_name} {emp.last_name}</span>
        </div>
      </td>
      {days.map(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const status = byDate[dateStr];
        const isWeekend = [0, 6].includes(getDay(day));
        return (
          <td key={dateStr} className="px-1 py-2 text-center">
            {isWeekend ? (
              <div className="w-5 h-5 mx-auto rounded-full bg-[var(--bg-muted)]"/>
            ) : status ? (
              <div title={status} className={cn('w-5 h-5 mx-auto rounded-full', STATUS_COLOR[status] || 'bg-gray-300')}/>
            ) : (
              <div className="w-5 h-5 mx-auto rounded-full border border-[var(--border)]"/>
            )}
          </td>
        );
      })}
    </tr>
  );
}

export default function AttendanceMonthlyPage() {
  const now = new Date();
  const [month, setMonth] = useState(format(now, 'yyyy-MM'));
  const { data: empResp } = useEmployees();
  const employees = (empResp?.data || []).slice(0, 20);

  const year = parseInt(month.split('-')[0]);
  const mon = parseInt(month.split('-')[1]) - 1;
  const days = eachDayOfInterval({ start: startOfMonth(new Date(year, mon, 1)), end: endOfMonth(new Date(year, mon, 1)) });

  const prev = () => {
    const d = new Date(year, mon - 1, 1);
    setMonth(format(d, 'yyyy-MM'));
  };
  const next = () => {
    const d = new Date(year, mon + 1, 1);
    setMonth(format(d, 'yyyy-MM'));
  };

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Monthly Calendar"
        breadcrumb={[{ label: 'Attendance', href: '/attendance' }, { label: 'Monthly View' }]}
      />

      <div className="card flex items-center justify-between p-3 max-w-xs">
        <button onClick={prev} className="p-2 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]">←</button>
        <span className="text-[14px] font-bold text-[var(--text-primary)]">{format(new Date(year, mon, 1), 'MMMM yyyy')}</span>
        <button onClick={next} className="p-2 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]">→</button>
      </div>

      <div className="flex gap-3 flex-wrap">
        {Object.entries(STATUS_COLOR).map(([s, cls]) => (
          <div key={s} className="flex items-center gap-1.5">
            <div className={cn('w-3 h-3 rounded-full', cls)}/>
            <span className="text-[11px] text-[var(--text-secondary)]">{s.replace('_', ' ')}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full border border-[var(--border)]"/>
          <span className="text-[11px] text-[var(--text-secondary)]">No Record</span>
        </div>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[var(--border)]">
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] sticky left-0 bg-[var(--bg-card)] z-10 border-r border-[var(--border)] min-w-[180px]">Employee</th>
              {days.map(day => (
                <th key={day.toISOString()} className={cn(
                  'px-1 py-2 text-[10px] font-semibold text-center min-w-[28px]',
                  [0, 6].includes(getDay(day)) ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-secondary)]'
                )}>{format(day, 'd')}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {employees.map(emp => <EmployeeRow key={emp.id} emp={emp} month={month} days={days}/>)}
            {employees.length === 0 && (
              <tr><td colSpan={days.length + 1} className="px-5 py-12 text-center text-[14px] text-[var(--text-tertiary)]">No employees</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
